import React from "react";
import { Box, Typography, Chip } from "@mui/material";
import { FiberManualRecord, StickyNote2Outlined } from "@mui/icons-material";
import ReactHtmlParser from "html-react-parser";
import { useAuth } from "hooks/useAuth";
import { localizeDateTimeMed } from "utils/dateFormats";
import OpenInNewTab from "components/Shared/OpenInNewTab";

const ShownToItem = ({ children }) => {
  return (
    <div className="rounded-sm bg-blue-100 px-2 py-1 text-xs dark:bg-slate-600">
      Shown to {children}
    </div>
  );
};

const NoteListItem = ({ note, hasReplies = false }) => {
  const { getAuthorName } = useAuth();

  const { isCustomer, isVendor, isContractor, isDriver } = useAuth();

  if (
    !note ||
    ((isCustomer || isVendor || isContractor || isDriver) && !note.showClient)
  )
    return null;

  const [contentBeforeLocation, locationContent] =
    note.content.split("Location");
  const location = locationContent
    ?.replace("Latitude", "")
    ?.replace("Longitude", "")
    ?.replace(":", "")
    ?.trim();

  const authorTypeLabel =
    {
      VendorProfile: "Vendor",
      VendorStaff: "Vendor Staff",
    }[note?.createdBy?.typeRefSource] || "";

  // Check if the note was created by a vendor
  const isVendorNote = ["VendorProfile", "VendorStaff"].includes(
    note?.createdBy?.typeRefSource
  );

  // Show pending only if: no replies AND not responded AND is vendor note
  const showPending = !hasReplies && !note?.responded && isVendorNote;

  return (
    <Box display="flex" alignItems="start" gap={2} padding={3}>
      <StickyNote2Outlined
        color="primary"
        fontSize="small"
        style={{ marginTop: 3 }}
      />
      <Box display="flex" flexGrow={1} flexDirection="column" gap={1}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography>
            {getAuthorName(note?.createdBy)}
            {authorTypeLabel && (
              <span style={{ color: "gray", fontSize: 13, marginLeft: "5px" }}>
                ( {authorTypeLabel} )
              </span>
            )}
          </Typography>
          {!!note?.type && (
            <>
              <FiberManualRecord style={{ color: "gray", fontSize: 10 }} />
              <Typography style={{ color: "var(--primary)" }}>
                {note?.source}
              </Typography>
              <FiberManualRecord style={{ color: "gray", fontSize: 10 }} />
              <Typography style={{ color: "var(--primary)" }}>
                {note?.type}
              </Typography>
            </>
          )}
          <FiberManualRecord style={{ color: "gray", fontSize: 10 }} />
          <Typography>{localizeDateTimeMed(note?.createdAt)}</Typography>

          {showPending && (
            <>
              <FiberManualRecord style={{ color: "gray", fontSize: 10 }} />
              <Chip
                label="Pending"
                size="small"
                color="warning"
                sx={{ height: 20, fontSize: 11 }}
              />
            </>
          )}

          {note.showClient &&
            !(isCustomer || isVendor || isContractor || isDriver) && (
              <ShownToItem>Client</ShownToItem>
            )}
        </Box>
        {contentBeforeLocation && (
          <Box>
            <Typography variant="body1">
              {ReactHtmlParser(contentBeforeLocation.trim())}
            </Typography>
          </Box>
        )}
        {location && (
          <Box sx={{ marginTop: 1 }}>
            <Typography variant="body2" sx={{ display: "inline" }}>
              {`Latitude: ${location
                ?.split(",")[0]
                ?.trim()}, Longitude: ${location?.split(",")[1]?.trim()} `}
            </Typography>
            <Typography
              variant="body2"
              sx={{ display: "inline", marginLeft: 1 }}
            >
              <OpenInNewTab
                link={`https://www.google.com/maps?q=${encodeURIComponent(
                  location
                )}`}
                label="View in Map"
              />
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NoteListItem;